// Cerebro: búsqueda + browser de todas las páginas del proyecto.

import { useEffect, useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { es } from 'date-fns/locale'
import { Search } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { useBrainPages, useBrainSearch } from '@/hooks/useBrain'
import {
  BRAIN_TEAM_LABELS,
  BRAIN_TEAMS,
  BRAIN_TYPE_COLORS,
  BRAIN_TYPE_LABELS,
  type BrainPageType,
  type BrainTeam,
} from '@/types/brain'

interface Props {
  onOpenPage: (slug: string) => void
}

const TYPE_FILTERS: Array<{ value: BrainPageType | 'all'; label: string }> = [
  { value: 'all', label: 'Todo' },
  { value: 'company', label: 'Cuentas' },
  { value: 'person', label: 'Personas' },
  { value: 'meeting', label: 'Llamadas' },
  { value: 'deal', label: 'Oportunidades' },
  { value: 'note', label: 'Notas' },
  { value: 'deck', label: 'Decks' },
  { value: 'concept', label: 'Conceptos' },
]

export function CerebroTab({ onOpenPage }: Props) {
  const [term, setTerm] = useState('')
  const [debouncedTerm, setDebouncedTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState<BrainPageType | 'all'>('all')
  // Sección por equipo (frontmatter.team, mig 202): comercial / producto /
  // tecnología / seguridad / legal. Ambos filtros van server-side — filtrar en
  // cliente sobre la ventana mixta de 200 daría vacíos falsos cuando los syncs
  // acumulen páginas.
  const [teamFilter, setTeamFilter] = useState<BrainTeam | 'all'>('all')
  const { data: pages, isLoading } = useBrainPages({
    pageType: typeFilter === 'all' ? undefined : typeFilter,
    team: teamFilter === 'all' ? undefined : teamFilter,
    limit: 200,
  })
  // One RPC per pause, not per keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedTerm(term), 300)
    return () => clearTimeout(t)
  }, [term])
  const search = useBrainSearch(debouncedTerm)

  const searching = term.trim().length >= 3
  const list = pages ?? []
  const ago = (iso: string) => formatDistanceToNow(new Date(iso), { addSuffix: true, locale: es })

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Buscar en el cerebro (mín. 3 letras)… ej: certificación, Ingenico, MDR"
          className="pl-9"
        />
      </div>

      {searching ? (
        <div className="space-y-2">
          {(search.isFetching || term.trim() !== debouncedTerm.trim()) && (
            <div className="py-6 text-center text-sm text-muted-foreground">Buscando…</div>
          )}
          {search.isError && (
            <div className="py-6 text-center text-sm text-destructive">
              La búsqueda falló — reintenta en unos segundos.
            </div>
          )}
          {!search.isFetching && term.trim() === debouncedTerm.trim() && search.data?.length === 0 && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Nada en el cerebro para “{term.trim()}”. Prueba reformular o pregunta directo en la pestaña Preguntar.
            </div>
          )}
          {(term.trim() === debouncedTerm.trim() ? (search.data ?? []) : []).map((hit) => (
            <Card
              key={hit.id}
              className="cursor-pointer p-3 transition-colors hover:bg-muted/50"
              onClick={() => onOpenPage(hit.slug)}
            >
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className={BRAIN_TYPE_COLORS[hit.page_type]}>
                  {BRAIN_TYPE_LABELS[hit.page_type]}
                </Badge>
                <span className="font-medium">{hit.title}</span>
                <span className="ml-auto shrink-0 text-xs text-muted-foreground">{ago(hit.updated_at)}</span>
              </div>
              {hit.snippet && <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{hit.snippet}</p>}
            </Card>
          ))}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-1.5">
            {TYPE_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setTypeFilter(f.value)}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                  typeFilter === f.value
                    ? 'border-teal-500 bg-teal-500/10 text-teal-600 dark:text-teal-400'
                    : 'border-border text-muted-foreground hover:bg-muted'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-muted-foreground">Equipo:</span>
            {(['all', ...BRAIN_TEAMS] as Array<BrainTeam | 'all'>).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTeamFilter(t)}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                  teamFilter === t
                    ? 'border-indigo-500 bg-indigo-500/10 text-indigo-600 dark:text-indigo-400'
                    : 'border-border text-muted-foreground hover:bg-muted'
                }`}
              >
                {t === 'all' ? 'Todos' : BRAIN_TEAM_LABELS[t]}
              </button>
            ))}
          </div>
          {isLoading && <div className="py-10 text-center text-sm text-muted-foreground">Cargando páginas…</div>}
          {!isLoading && list.length === 0 && (
            <div className="py-10 text-center text-sm text-muted-foreground">
              El cerebro no tiene páginas de este tipo todavía.
            </div>
          )}
          <div className="space-y-2">
            {list.map((p) => (
              <Card
                key={p.id}
                className="cursor-pointer p-3 transition-colors hover:bg-muted/50"
                onClick={() => onOpenPage(p.slug)}
              >
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className={BRAIN_TYPE_COLORS[p.page_type]}>
                    {BRAIN_TYPE_LABELS[p.page_type]}
                  </Badge>
                  {typeof p.frontmatter?.team === 'string' && p.frontmatter.team in BRAIN_TEAM_LABELS && (
                    <Badge variant="outline" className="text-muted-foreground">
                      {BRAIN_TEAM_LABELS[p.frontmatter.team as BrainTeam]}
                    </Badge>
                  )}
                  <span className="truncate font-medium">{p.title}</span>
                  <span className="ml-auto hidden shrink-0 font-mono text-xs text-muted-foreground sm:inline">{p.slug}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">{ago(p.updated_at)}</span>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
